import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { bankAccountAPI } from '../services/api';
import { useToast } from '../components/Toast';
import { useAuth } from '../context/AuthContext';

function BankAccountDetails() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [bankAccount, setBankAccount] = useState(null);
  const [payments, setPayments] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [filteredPayments, setFilteredPayments] = useState([]);

  useEffect(() => {
    if (user && user.role !== 'admin') {
      showToast('Access denied. Admin only.', 'error');
      navigate('/user');
    }
  }, [user, navigate, showToast]);

  useEffect(() => {
    fetchTransactions();
  }, [id]);

  useEffect(() => {
    setFilteredPayments(payments);
  }, [payments]);

  const fetchTransactions = async (dateFilters = {}) => {
    setLoading(true);
    try {
      const response = await bankAccountAPI.getBankAccountTransactions(id, dateFilters);
      setBankAccount(response.data.bankAccount);
      setPayments(response.data.payments);
      setStatistics(response.data.statistics);
      setFilteredPayments(response.data.payments);
    } catch (error) {
      console.error('Failed to fetch bank account transactions:', error);
      showToast(error.response?.data?.message || 'Failed to fetch transactions', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDateFilter = () => {
    const filters = {};
    if (startDate) filters.startDate = startDate;
    if (endDate) filters.endDate = endDate;
    fetchTransactions(filters);
  };

  const handleClearFilter = () => {
    setStartDate('');
    setEndDate('');
    fetchTransactions();
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatDateTime = (date) => {
    return new Date(date).toLocaleString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div style={{ 
        minHeight: '90vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center' 
      }}>
        <p style={{ fontSize: '18px', color: '#718096' }}>Loading...</p>
      </div>
    );
  }

  if (!bankAccount) {
    return (
      <div style={{ 
        minHeight: '90vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center' 
      }}>
        <p style={{ fontSize: '18px', color: '#718096' }}>Bank account not found</p>
      </div>
    );
  }

  return (
    <div style={{ 
      minHeight: '90vh', 
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      padding: '40px 20px'
    }}>
      <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
        {/* Header with Back Button */}
        <div style={{ marginBottom: '24px' }}>
          <button
            onClick={() => navigate('/admin/bank-accounts')}
            style={{
              padding: '10px 20px',
              fontSize: '14px',
              fontWeight: '600',
              background: 'white',
              color: '#667eea',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            ← Back to Bank Accounts
          </button>
        </div>

        {/* Bank Account Info */}
        <div style={{ 
          background: 'white',
          borderRadius: '16px',
          padding: '32px',
          marginBottom: '24px',
          boxShadow: '0 10px 30px rgba(0,0,0,0.1)'
        }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'flex-start',
            marginBottom: '24px'
          }}>
            <div>
              <h1 style={{ 
                fontSize: '28px', 
                fontWeight: '700',
                color: '#2d3748',
                marginBottom: '8px'
              }}>
                🏦 {bankAccount.accountName}
              </h1>
              <p style={{ fontSize: '14px', color: '#718096' }}>
                Transaction History
              </p>
            </div>
            <span style={{
              padding: '8px 16px',
              fontSize: '14px',
              fontWeight: '600',
              borderRadius: '20px',
              background: bankAccount.isActive ? '#d1fae5' : '#fee2e2',
              color: bankAccount.isActive ? '#065f46' : '#991b1b'
            }}>
              {bankAccount.isActive ? '✓ Active' : '✕ Inactive'}
            </span>
          </div>

          <div style={{ 
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '20px',
            padding: '20px',
            background: '#f7fafc',
            borderRadius: '12px'
          }}>
            <div>
              <p style={{ fontSize: '12px', color: '#718096', marginBottom: '4px', fontWeight: '600' }}>
                Bank Name
              </p>
              <p style={{ fontSize: '16px', color: '#2d3748', fontWeight: '600' }}>
                {bankAccount.bankName}
              </p>
            </div>
            <div>
              <p style={{ fontSize: '12px', color: '#718096', marginBottom: '4px', fontWeight: '600' }}>
                Account Number
              </p>
              <p style={{ fontSize: '16px', color: '#2d3748', fontWeight: '600', fontFamily: 'monospace' }}>
                {bankAccount.accountNumber}
              </p>
            </div>
            <div>
              <p style={{ fontSize: '12px', color: '#718096', marginBottom: '4px', fontWeight: '600' }}>
                IFSC Code
              </p>
              <p style={{ fontSize: '16px', color: '#2d3748', fontWeight: '600', fontFamily: 'monospace' }}>
                {bankAccount.ifscCode}
              </p>
            </div>
            <div>
              <p style={{ fontSize: '12px', color: '#718096', marginBottom: '4px', fontWeight: '600' }}>
                Branch
              </p>
              <p style={{ fontSize: '16px', color: '#2d3748' }}>
                {bankAccount.branch}
              </p>
            </div>
            {bankAccount.upiId && (
              <div>
                <p style={{ fontSize: '12px', color: '#718096', marginBottom: '4px', fontWeight: '600' }}>
                  UPI ID
                </p>
                <p style={{ fontSize: '16px', color: '#667eea', fontWeight: '600', fontFamily: 'monospace' }}>
                  {bankAccount.upiId}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Statistics */}
        {statistics && (
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '16px',
            marginBottom: '24px'
          }}>
            <div style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              padding: '24px',
              borderRadius: '12px',
              color: 'white'
            }}>
              <p style={{ fontSize: '14px', opacity: 0.9, marginBottom: '8px' }}>Total Transactions</p>
              <p style={{ fontSize: '32px', fontWeight: '700' }}>{statistics.paymentCount}</p>
            </div>
            
            <div style={{
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              padding: '24px',
              borderRadius: '12px',
              color: 'white'
            }}>
              <p style={{ fontSize: '14px', opacity: 0.9, marginBottom: '8px' }}>Total Amount Received</p>
              <p style={{ fontSize: '32px', fontWeight: '700' }}>{formatCurrency(statistics.totalAmount)}</p>
            </div>

            <div style={{
              background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
              padding: '24px',
              borderRadius: '12px',
              color: 'white'
            }}>
              <p style={{ fontSize: '14px', opacity: 0.9, marginBottom: '8px' }}>Payment Methods</p>
              <div style={{ marginTop: '8px' }}>
                {Object.entries(statistics.paymentMethods).map(([method, amount]) => (
                  <div key={method} style={{ fontSize: '13px', marginBottom: '4px' }}>
                    {method.toUpperCase()}: {formatCurrency(amount)}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Date Range Filter */}
        <div style={{ 
          background: 'white',
          borderRadius: '16px',
          padding: '24px',
          marginBottom: '24px',
          boxShadow: '0 10px 30px rgba(0,0,0,0.1)'
        }}>
          <h2 style={{ 
            fontSize: '18px', 
            fontWeight: '600',
            color: '#2d3748',
            marginBottom: '16px'
          }}>
            📅 Filter by Date Range
          </h2>
          
          <div style={{ 
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '16px',
            alignItems: 'end'
          }}>
            <div>
              <label style={{ 
                display: 'block', 
                fontSize: '14px', 
                fontWeight: '600',
                color: '#4a5568',
                marginBottom: '8px'
              }}>
                Start Date
              </label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  fontSize: '14px',
                  border: '2px solid #e2e8f0',
                  borderRadius: '8px',
                  outline: 'none'
                }}
              />
            </div>

            <div>
              <label style={{ 
                display: 'block', 
                fontSize: '14px', 
                fontWeight: '600',
                color: '#4a5568',
                marginBottom: '8px'
              }}>
                End Date
              </label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  fontSize: '14px',
                  border: '2px solid #e2e8f0',
                  borderRadius: '8px',
                  outline: 'none'
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={handleDateFilter}
                style={{
                  padding: '10px 24px',
                  fontSize: '14px',
                  fontWeight: '600',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer'
                }}
              >
                Apply Filter
              </button>
              <button
                onClick={handleClearFilter}
                style={{
                  padding: '10px 24px',
                  fontSize: '14px',
                  fontWeight: '600',
                  background: '#e2e8f0',
                  color: '#4a5568',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer'
                }}
              >
                Clear
              </button>
            </div>
          </div>
        </div>

        {/* Transactions List */}
        <div style={{ 
          background: 'white',
          borderRadius: '16px',
          padding: '24px',
          boxShadow: '0 10px 30px rgba(0,0,0,0.1)'
        }}>
          <h2 style={{ 
            fontSize: '20px', 
            fontWeight: '600',
            color: '#2d3748',
            marginBottom: '20px'
          }}>
            💳 Transactions ({filteredPayments.length})
          </h2>

          {filteredPayments.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: '60px 20px',
              color: '#718096'
            }}>
              <p style={{ fontSize: '48px', marginBottom: '16px' }}>💳</p>
              <p style={{ fontSize: '16px', fontWeight: '600', marginBottom: '8px' }}>
                No transactions found
              </p>
              <p style={{ fontSize: '14px' }}>
                {startDate || endDate 
                  ? 'Try adjusting your date range filter' 
                  : 'No payments have been recorded to this account yet'}
              </p>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f7fafc', borderBottom: '2px solid #e2e8f0' }}>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#4a5568' }}>
                      Receipt #
                    </th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#4a5568' }}>
                      Date
                    </th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#4a5568' }}>
                      Student
                    </th>
                    <th style={{ padding: '12px 16px', textAlign: 'right', fontSize: '12px', fontWeight: '600', color: '#4a5568' }}>
                      Amount
                    </th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#4a5568' }}>
                      Method
                    </th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#4a5568' }}>
                      Transaction ID
                    </th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#4a5568' }}>
                      Recorded By
                    </th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#4a5568' }}>
                      Notes
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPayments.map((payment, index) => (
                    <tr 
                      key={payment._id}
                      style={{ 
                        backgroundColor: index % 2 === 0 ? 'white' : '#f9fafb',
                        borderBottom: '1px solid #e2e8f0'
                      }}
                    >
                      <td style={{ padding: '12px 16px', fontSize: '13px', fontWeight: '600', color: '#667eea' }}>
                        {payment.receiptNumber}
                      </td>
                      <td style={{ padding: '12px 16px', fontSize: '13px', color: '#2d3748' }}>
                        {formatDate(payment.paymentDate)}
                      </td>
                      <td style={{ padding: '12px 16px', fontSize: '13px' }}>
                        <div>
                          <div style={{ fontWeight: '600', color: '#2d3748' }}>
                            {payment.user?.name || 'N/A'}
                          </div>
                          <div style={{ fontSize: '12px', color: '#718096' }}>
                            {payment.user?.email || ''}
                          </div>
                        </div>
                      </td>
                      <td style={{ padding: '12px 16px', fontSize: '14px', fontWeight: '700', textAlign: 'right', color: '#10b981' }}>
                        {formatCurrency(payment.amount)}
                      </td>
                      <td style={{ padding: '12px 16px', fontSize: '13px' }}>
                        <span style={{
                          padding: '4px 10px',
                          backgroundColor: '#eef2ff',
                          color: '#667eea',
                          borderRadius: '6px',
                          fontSize: '12px',
                          fontWeight: '600',
                          textTransform: 'capitalize'
                        }}>
                          {payment.paymentMethod.replace('-', ' ')}
                        </span>
                      </td>
                      <td style={{ padding: '12px 16px', fontSize: '13px', color: '#718096', fontFamily: 'monospace' }}>
                        {payment.transactionId || '-'}
                      </td>
                      <td style={{ padding: '12px 16px', fontSize: '13px', color: '#2d3748' }}>
                        {payment.recordedBy?.name || 'System'}
                      </td>
                      <td style={{ padding: '12px 16px', fontSize: '13px', color: '#718096' }}>
                        {payment.notes || '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default BankAccountDetails;
